"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Icon, Input, InputGroup, Spinner } from "@chakra-ui/react";
import { CheckCircle, WarningCircle } from "iconoir-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { useDebounce } from "../../../hooks/useDebounce";
import { buildUrlValidationSummary, detectSocialPlatform, isLikelyReachableUrl, isValidUrlFormat, normalizeUrl, } from "../../../lib/utils/url-validation";
import { Field } from "../components/field";
const DEFAULT_DEBOUNCE_MS = 500;
const DEFAULT_MESSAGES = {
    invalidFormat: "Invalid URL format. Example: https://instagram.com/yourprofile or https://mysite.com",
    invalidDomain: "The domain name is invalid. Check the spelling (e.g. .com, .fr, .io)",
    notReachable: "This URL does not seem reachable",
    pageGone: (httpStatus) => `The site answered with status ${httpStatus}`,
    profileUnavailable: "This profile seems unavailable",
    unverifiable: "This URL could not be verified",
};
function unreachableMessage(messages, reason, httpStatus) {
    switch (reason) {
        case "page_gone":
            return httpStatus ? messages.pageGone(httpStatus) : messages.unverifiable;
        case "profile_unavailable":
            return messages.profileUnavailable;
        case "not_public":
            return messages.notReachable;
        default:
            return messages.unverifiable;
    }
}
function URLField({ name, label, placeholder, helperText, disclaimer, defaultValue = "", required, disabled, invalid, errorText, autoFocus, size, onValidationChange, messages: messageOverrides, }) {
    const messagesRef = useRef(DEFAULT_MESSAGES);
    messagesRef.current = { ...DEFAULT_MESSAGES, ...messageOverrides };
    const [value, setValue] = useState(defaultValue);
    const debouncedValue = useDebounce(value, DEFAULT_DEBOUNCE_MS);
    const [normalizedValue, setNormalizedValue] = useState(() => defaultValue ? normalizeUrl(defaultValue) : "");
    const [validationStatus, setValidationStatus] = useState("idle");
    const [reachabilityStatus, setReachabilityStatus] = useState("unknown");
    const [message, setMessage] = useState();
    const fetchController = useRef(null);
    useEffect(() => {
        return () => {
            fetchController.current?.abort();
        };
    }, []);
    const hasClientValidationError = validationStatus === "invalid";
    const hasServerValidationError = reachabilityStatus === "unreachable";
    const derivedInvalid = invalid || hasClientValidationError || hasServerValidationError;
    const derivedErrorText = useMemo(() => {
        if (errorText) {
            return errorText;
        }
        if (hasClientValidationError) {
            return message || messagesRef.current.invalidFormat;
        }
        if (hasServerValidationError) {
            return message || messagesRef.current.unverifiable;
        }
        return undefined;
    }, [errorText, hasClientValidationError, hasServerValidationError, message]);
    const runValidationCallback = useCallback((state) => {
        onValidationChange?.(state);
    }, [onValidationChange]);
    useEffect(() => {
        setValue(defaultValue);
        setNormalizedValue(defaultValue ? normalizeUrl(defaultValue) : "");
        setValidationStatus("idle");
    }, [defaultValue]);
    useEffect(() => {
        if (!debouncedValue) {
            setNormalizedValue("");
            setValidationStatus("idle");
            setReachabilityStatus("unknown");
            setMessage(undefined);
            runValidationCallback({
                inputValue: "",
                normalizedValue: "",
                validationStatus: "idle",
                formatValid: false,
                platform: null,
                reachability: "unknown",
            });
            return;
        }
        const trimmed = debouncedValue.trim();
        if (!trimmed) {
            setNormalizedValue("");
            setValidationStatus("idle");
            setReachabilityStatus("unknown");
            setMessage(undefined);
            runValidationCallback({
                inputValue: debouncedValue,
                normalizedValue: "",
                validationStatus: "idle",
                formatValid: false,
                platform: null,
                reachability: "unknown",
            });
            return;
        }
        const normalized = normalizeUrl(trimmed);
        const formatValid = isValidUrlFormat(normalized);
        setNormalizedValue(normalized);
        setReachabilityStatus("unknown");
        setValidationStatus("validating");
        setMessage(undefined);
        if (!formatValid) {
            const errorMsg = messagesRef.current.invalidFormat;
            setValidationStatus("invalid");
            setMessage(errorMsg);
            runValidationCallback({
                inputValue: debouncedValue,
                normalizedValue: normalized,
                validationStatus: "invalid",
                formatValid: false,
                platform: null,
                reachability: "unknown",
                message: errorMsg,
            });
            return;
        }
        const summary = buildUrlValidationSummary(normalized);
        if (!summary.isDomainValid) {
            const errorMsg = messagesRef.current.invalidDomain;
            setValidationStatus("invalid");
            setMessage(errorMsg);
            runValidationCallback({
                inputValue: debouncedValue,
                normalizedValue: summary.normalizedUrl,
                validationStatus: "invalid",
                formatValid: false,
                platform: summary.platform,
                reachability: "unknown",
                message: errorMsg,
            });
            return;
        }
        if (!isLikelyReachableUrl(summary.normalizedUrl)) {
            const errorMsg = messagesRef.current.notReachable;
            setValidationStatus("invalid");
            setMessage(errorMsg);
            runValidationCallback({
                inputValue: debouncedValue,
                normalizedValue: summary.normalizedUrl,
                validationStatus: "invalid",
                formatValid: true,
                platform: summary.platform,
                reachability: "unknown",
                message: errorMsg,
            });
            return;
        }
        setValidationStatus("validating");
        setReachabilityStatus("checking");
        runUrlReachabilityCheck(summary.normalizedUrl).catch(() => {
            // Errors handled in runUrlReachabilityCheck
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue, runValidationCallback]);
    useEffect(() => {
        runValidationCallback({
            inputValue: value,
            normalizedValue,
            validationStatus,
            formatValid: validationStatus === "valid" || validationStatus === "validating",
            platform: detectSocialPlatform(normalizedValue),
            reachability: reachabilityStatus,
            message,
        });
    }, [
        value,
        normalizedValue,
        validationStatus,
        reachabilityStatus,
        message,
        runValidationCallback,
    ]);
    const runUrlReachabilityCheck = useCallback(async (urlToCheck) => {
        if (fetchController.current) {
            fetchController.current.abort();
        }
        const controller = new AbortController();
        fetchController.current = controller;
        try {
            const response = await fetch(`/api/validate-url?url=${encodeURIComponent(urlToCheck)}`, {
                method: "GET",
                signal: controller.signal,
            });
            if (!response.ok) {
                throw new Error("Request failed");
            }
            const payload = await response.json();
            if (payload.status === "reachable") {
                setValidationStatus("valid");
                setReachabilityStatus("reachable");
                setMessage(undefined);
                runValidationCallback({
                    inputValue: value,
                    normalizedValue: urlToCheck,
                    validationStatus: "valid",
                    formatValid: true,
                    platform: detectSocialPlatform(urlToCheck),
                    reachability: "reachable",
                });
            }
            else if (payload.status === "unknown") {
                setValidationStatus("valid");
                setReachabilityStatus("unknown");
                setMessage(undefined);
            }
            else {
                setValidationStatus("invalid");
                setReachabilityStatus("unreachable");
                setMessage(unreachableMessage(messagesRef.current, payload.reason, payload.httpStatus));
            }
        }
        catch (error) {
            if (error.name === "AbortError") {
                return;
            }
            setValidationStatus("valid");
            setReachabilityStatus("unknown");
            setMessage(undefined);
        }
        finally {
            if (fetchController.current === controller) {
                fetchController.current = null;
            }
        }
    }, [runValidationCallback, value]);
    const renderStatusIcon = () => {
        if (validationStatus === "validating" ||
            reachabilityStatus === "checking") {
            return _jsx(Spinner, { size: "sm", boxSize: 4 });
        }
        if (validationStatus === "valid") {
            return _jsx(Icon, { as: CheckCircle, color: "green.500", boxSize: 4 });
        }
        if (derivedInvalid) {
            return _jsx(Icon, { as: WarningCircle, color: "red.500", boxSize: 4 });
        }
        return null;
    };
    return (_jsxs(Field, { label: label, required: required, helperText: helperText, invalid: derivedInvalid, errorText: derivedErrorText, children: [_jsx(InputGroup, { endElement: renderStatusIcon(), children: _jsx(Input, { name: name, placeholder: placeholder, value: value, onChange: (event) => setValue(event.target.value), onBlur: () => {
                        if (normalizedValue && validationStatus === "valid") {
                            setValue(normalizedValue);
                        }
                    }, disabled: disabled, required: required, autoFocus: autoFocus, size: size, type: "url", "aria-invalid": derivedInvalid, "aria-describedby": derivedErrorText
                        ? `${name}-error`
                        : helperText
                            ? `${name}-helper`
                            : undefined, "aria-busy": validationStatus === "validating" ||
                        reachabilityStatus === "checking" }) }), disclaimer && (_jsx(Box, { mt: 2, fontSize: "sm", color: "fg.solid", children: disclaimer }))] }));
}
export default memo(URLField);
